import React from "react";
import styled, { css } from "styled-components";

import { fileIcon as FileIcon } from "../../assets/icons";
import {
  buildAssignmentPlayerUrl,
  buildLessonPlayerUrl,
} from "../../services/studentPortalService/studentPortalService";
import type { StudentMediaFile } from "../../services/studentPortalService/studentPortalService.types";
import { pxToRem } from "../../styles/utils";
import {
  MediaAction,
  MediaIcon,
  MediaItems,
  MediaMain,
  MediaName,
  MediaProgress,
  MediaProgressFill,
  MediaRowLink,
  MediaRowStatic,
  MediaSub,
} from "./StudentCourseLesson.styles";
import { ExternalLink, MediaItem, MediaList } from "./studentPortal.styles";

export interface StudentMediaVideoSelection {
  id: number;
  name: string;
  playerUrl: string;
}

interface StudentMediaListProps {
  items: StudentMediaFile[];
  lessonId?: number;
  assignmentId?: number;
  variant?: "default" | "lesson";
  leadingItem?: React.ReactNode;
  selectedVideoId?: number | null;
  onVideoSelect?: (video: StudentMediaVideoSelection) => void;
}

const SelectableMediaRow = styled.button<{ $selected: boolean }>`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding: ${({ theme }) => pxToRem(theme.spacing.xs)} ${({ theme }) => pxToRem(theme.spacing.sm)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.white};
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.brandSubtle};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }

  ${({ $selected, theme }) =>
    $selected &&
    css`
      border-color: ${theme.colors.brand};
      background: ${theme.colors.brandSubtle};
      box-shadow: inset ${pxToRem(3)} 0 0 ${theme.colors.brand};
    `}
`;

const resolveMedia = (
  item: StudentMediaFile,
  lessonId?: number,
  assignmentId?: number,
) => {
  const playerUrl = item.type === "video"
    ? lessonId
      ? buildLessonPlayerUrl(lessonId, item.id)
      : assignmentId
        ? buildAssignmentPlayerUrl(assignmentId, item.id)
        : null
    : null;
  return {
    href: item.type === "document" ? item.url : playerUrl,
    unavailable: item.type === "video" && item.status !== "ready",
  };
};

const StudentMediaList: React.FC<StudentMediaListProps> = ({
  items,
  lessonId,
  assignmentId,
  variant = "default",
  leadingItem,
  selectedVideoId,
  onVideoSelect,
}) => {
  if (!items.length && !leadingItem) return null;

  if (variant === "default") {
    return (
      <MediaList>
        {items.map((item) => {
          const { href, unavailable } = resolveMedia(item, lessonId, assignmentId);
          return (
            <MediaItem key={`${item.type}-${item.id}`}>
              <span>{item.name}</span>
              {unavailable ? (
                <span>Видео обрабатывается{typeof item.progress_percent === "number" ? ` · ${item.progress_percent}%` : ""}</span>
              ) : href ? (
                <ExternalLink href={href} target="_blank" rel="noreferrer">
                  {item.type === "video" ? "Смотреть" : "Скачать"}
                </ExternalLink>
              ) : null}
            </MediaItem>
          );
        })}
      </MediaList>
    );
  }

  return (
    <MediaItems>
      {leadingItem}
      {items.map((item) => {
        const { href, unavailable } = resolveMedia(item, lessonId, assignmentId);
        const progress = typeof item.progress_percent === "number" ? item.progress_percent : 0;
        const selected = item.type === "video" && selectedVideoId === item.id;
        const body = (
          <>
            <MediaIcon><FileIcon aria-hidden="true" /></MediaIcon>
            <MediaMain>
              <MediaName>{item.name}</MediaName>
              {unavailable ? (
                <>
                  <MediaSub>
                    Видео обрабатывается{typeof item.progress_percent === "number" ? `, ${item.progress_percent}%` : ""}
                  </MediaSub>
                  {typeof item.progress_percent === "number" ? (
                    <MediaProgress>
                      <MediaProgressFill $percent={progress} />
                    </MediaProgress>
                  ) : null}
                </>
              ) : (
                <MediaSub>{item.type === "video" ? "видео" : "документ"}</MediaSub>
              )}
            </MediaMain>
            {!unavailable && href ? (
              <MediaAction>
                {item.type === "video" ? (selected ? "Открыто справа" : "Смотреть") : "Скачать"}
              </MediaAction>
            ) : null}
          </>
        );

        if (item.type === "video" && !unavailable && href && onVideoSelect) {
          return (
            <SelectableMediaRow
              key={`${item.type}-${item.id}`}
              type="button"
              $selected={selected}
              aria-pressed={selected}
              onClick={() => onVideoSelect({ id: item.id, name: item.name, playerUrl: href })}
            >
              {body}
            </SelectableMediaRow>
          );
        }

        return unavailable || !href ? (
          <MediaRowStatic key={`${item.type}-${item.id}`}>{body}</MediaRowStatic>
        ) : (
          <MediaRowLink key={`${item.type}-${item.id}`} href={href} target="_blank" rel="noreferrer">
            {body}
          </MediaRowLink>
        );
      })}
    </MediaItems>
  );
};

export default StudentMediaList;
